import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  PHONE_COUNTRIES,
  findCountryByCode,
  findCountryByDialCode,
  formatPhoneNumber,
  toE164Format
} from '../../services/PhoneCountryService';
import { ChevronUpDownIcon, CheckIcon } from '@heroicons/react/20/solid';
import ShipPlayLogo from 'images/shipplay/logos/Ship&Play_FullColor_LightBG.svg';

const STEPS = {
  SENDING: 'sending',
  VERIFY: 'verify',
  UPDATE_PHONE: 'update_phone',
  SUCCESS: 'success'
};

const MobileVerifyModal = ({
  isOpen,
  onClose,
  onVerified,
  phoneNumber,
  authToken,
  brandId
}) => {
  const [step, setStep] = useState(STEPS.SENDING);
  const [verificationCode, setVerificationCode] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);
  const [smsTrackingOptin, setSmsTrackingOptin] = useState(false);

  // Update phone state
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [localPhoneNumber, setLocalPhoneNumber] = useState('');
  const [selectedPhoneCountry, setSelectedPhoneCountry] = useState(
    findCountryByCode('US')
  );
  const [isPhoneDropdownOpen, setIsPhoneDropdownOpen] = useState(false);
  const [phoneSearchTerm, setPhoneSearchTerm] = useState('');

  const filteredPhoneCountries = useMemo(() => {
    if (!phoneSearchTerm) return PHONE_COUNTRIES;
    return PHONE_COUNTRIES.filter(country =>
      country.name.toLowerCase().includes(phoneSearchTerm.toLowerCase()) ||
      country.dialCode.includes(phoneSearchTerm)
    );
  }, [phoneSearchTerm]);

  const getHeaders = useCallback(() => {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    const headers = { 'Content-Type': 'application/json' };
    if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
    if (authToken) headers['x-user-token'] = authToken;
    return headers;
  }, [authToken]);

  const sendVerificationCode = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/api/v5/users/mobile_verify', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ brand_id: brandId })
      });

      const data = await response.json();

      if (data.success) {
        if (data.phone_number) {
          setMaskedPhone(data.phone_number);
        }
        setStep(STEPS.VERIFY);
        startResendCountdown();
      } else if (response.status === 404) {
        // Phone number missing or invalid — show update phone screen
        setStep(STEPS.UPDATE_PHONE);
        if (data.error === 'invalid_number') {
          setError('The phone number on file is not valid. Please enter a new one.');
        }
      } else {
        setError(data.message || 'Failed to send verification code.');
        setStep(STEPS.VERIFY);
      }
    } catch (err) {
      console.error('Send verification error:', err);
      setError('Failed to send verification code. Please try again.');
      setStep(STEPS.VERIFY);
    } finally {
      setLoading(false);
    }
  }, [getHeaders, brandId]);

  // Verify the code
  const submitVerificationCode = useCallback(async () => {
    if (verificationCode.length !== 6) {
      setError('Please enter a 6-digit verification code.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await fetch('/api/v5/users/mobile_verify/check', {
        method: 'PATCH',
        headers: getHeaders(),
        body: JSON.stringify({
          brand_id: brandId,
          code: verificationCode
        })
      });

      const data = await response.json();

      if (data.status === 'code_approved') {
        setStep(STEPS.SUCCESS);
      } else if (data.status === 'code_expired') {
        setError('The code has expired. Please click "Resend code" to get a new one.');
      } else {
        setError('Incorrect code. Please try again.');
      }
    } catch (err) {
      console.error('Verify code error:', err);
      setError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [verificationCode, getHeaders, brandId]);

  // Update phone number and resend code
  const updatePhoneAndSend = useCallback(async () => {
    const phone = newPhoneNumber || toE164Format(selectedPhoneCountry.dialCode, localPhoneNumber);
    if (!phone) {
      setError('Please enter a phone number.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await fetch('/api/v5/users/mobile_verify/update_phone', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          brand_id: brandId,
          phone_number: phone
        })
      });

      const data = await response.json();

      if (data.success) {
        if (data.phone_number) {
          setMaskedPhone(data.phone_number);
        }
        setVerificationCode('');
        setStep(STEPS.VERIFY);
        startResendCountdown();
      } else {
        setError(data.message || 'Failed to update phone number. Please try again.');
      }
    } catch (err) {
      console.error('Update phone error:', err);
      setError('Failed to update phone number. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [newPhoneNumber, localPhoneNumber, selectedPhoneCountry, getHeaders, brandId]);

  // Resend countdown
  const startResendCountdown = () => {
    setResendCountdown(30);
  };

  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = setTimeout(() => setResendCountdown(prev => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCountdown]);

  // Send code when modal opens
  useEffect(() => {
    if (isOpen && authToken) {
      setStep(STEPS.SENDING);
      setVerificationCode('');
      setError('');
      sendVerificationCode();
    }
  }, [isOpen, authToken]);

  // Close on escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleSkip();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    if (phoneNumber && phoneNumber.startsWith('+')) {
      const country = findCountryByDialCode(phoneNumber);
      if (country) {
        setSelectedPhoneCountry(country);
        const local = phoneNumber.substring(country.dialCode.length);
        setLocalPhoneNumber(formatPhoneNumber(local, country.format));
        setNewPhoneNumber(phoneNumber);
      }
    }
  }, [phoneNumber]);

  const handleSkip = () => {
    onClose();
  };

  const handleDone = () => {
    if (onVerified) {
      onVerified({ smsTrackingOptin });
    }
    onClose();
  };

  const handleResend = () => {
    if (resendCountdown > 0) return;
    setVerificationCode('');
    setError('');
    sendVerificationCode();
  };

  const handlePhoneCountrySelect = (country) => {
    setSelectedPhoneCountry(country);
    setIsPhoneDropdownOpen(false);
    setPhoneSearchTerm('');

    const cleaned = localPhoneNumber.replace(/\D/g, '');
    const reformatted = formatPhoneNumber(cleaned, country.format);
    setLocalPhoneNumber(reformatted);
    setNewPhoneNumber(toE164Format(country.dialCode, cleaned));
  };

  const handlePhoneNumberChange = (e) => {
    const input = e.target.value;
    const formatted = formatPhoneNumber(input, selectedPhoneCountry.format);
    setLocalPhoneNumber(formatted);
    setNewPhoneNumber(toE164Format(selectedPhoneCountry.dialCode, input));
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleSkip();
    }
  };

  const displayPhone = maskedPhone || (phoneNumber ? phoneNumber.replace(/\d(?=\d{4})/g, '#') : '');

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-[480px] mx-4 relative">
        {/* Close Button */}
        <button
          type="button"
          onClick={handleSkip}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 z-10"
          aria-label="Close"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div className="p-8">
          {/* Brand Logo */}
          <div className="flex justify-center mb-6">
            <img
              src={ShipPlayLogo}
              alt="Ship&Play"
              className="h-10"
            />
          </div>

          {step === STEPS.SENDING && (
            <div className="text-center">
              <p
                className="text-[16px] font-normal text-[#252525]"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                Sending verification code...
              </p>
            </div>
          )}

          {step === STEPS.VERIFY && (
            <>
              <h2
                className="text-[20px] font-semibold leading-[26px] text-[#252525] mb-2"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                We just sent a code to your phone.
              </h2>
              <p
                className="text-[14px] font-normal text-[#6E6E6E] mb-6"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                For security purposes, verify your phone number ({displayPhone}).
              </p>

              <div className="flex justify-between items-center mb-2">
                <label
                  className="text-[12px] font-normal text-[#252525]"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  Verification code
                </label>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resendCountdown > 0}
                  className={`text-[12px] font-semibold underline ${
                    resendCountdown > 0
                      ? 'text-[#6E6E6E] cursor-not-allowed'
                      : 'text-[#252525] cursor-pointer'
                  } bg-transparent border-none`}
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  Resend code{resendCountdown > 0 ? ` (${resendCountdown}s)` : ''}
                </button>
              </div>

              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="Enter verification code"
                value={verificationCode}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setVerificationCode(val);
                  setError('');
                }}
                disabled={loading}
                className="
                  w-full h-[56px] px-4 text-[14px] font-normal leading-[18px] text-[#252525]
                  bg-white border border-gray-300 rounded-xl mb-6
                  focus:outline-none focus:ring-2 focus:ring-[#49C44F] focus:border-transparent
                  placeholder:text-[#6E6E6E]
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
                style={{ fontFamily: 'Poppins, sans-serif' }}
              />

              {error && (
                <p
                  className="text-[12px] text-red-600 mb-4"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  {error}
                </p>
              )}

              <button
                type="button"
                onClick={submitVerificationCode}
                disabled={loading || verificationCode.length !== 6}
                className={`
                  w-full h-[48px] text-white font-semibold text-[16px] leading-[20px]
                  bg-[rgba(28,41,56,1)] rounded-lg mb-4
                  ${loading || verificationCode.length !== 6
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-[#3a3a3a] cursor-pointer'}
                `}
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {loading ? 'Verifying...' : 'Verify'}
              </button>

              <button
                type="button"
                onClick={handleSkip}
                className="w-full h-[48px] text-[#252525] font-semibold text-[16px] leading-[20px]
                  bg-white border border-[#252525] rounded-lg mb-4
                  hover:bg-gray-50 cursor-pointer"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                Skip for now
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setError('');
                    setStep(STEPS.UPDATE_PHONE);
                  }}
                  className="text-[14px] font-semibold text-[rgba(28,41,56,1)] underline
                    bg-transparent border-none cursor-pointer"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  Need to update your phone number?
                </button>
              </div>
            </>
          )}
          {step === STEPS.UPDATE_PHONE && (
            <>
              <h2
                className="text-[20px] font-semibold leading-[26px] text-[#252525] mb-6"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                Re-enter your phone number.
              </h2>

              <label
                className="text-[12px] font-normal text-[#252525] mb-2 block"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                Phone number*
              </label>

              <div className="relative mb-4">
                <div className="flex h-[56px] bg-white border border-gray-300 rounded-lg
                  focus-within:ring-2 focus-within:ring-[#49C44F] focus-within:border-transparent
                  overflow-hidden">
                  {selectedPhoneCountry && (
                    <button
                      type="button"
                      onClick={() => setIsPhoneDropdownOpen(!isPhoneDropdownOpen)}
                      className="flex items-center gap-2 px-3 border-r border-gray-300
                        hover:bg-gray-50 transition-colors"
                      style={{ fontFamily: 'Poppins, sans-serif' }}
                    >
                      <span className="text-xl">{selectedPhoneCountry.flag}</span>
                      <span className="text-[14px] font-normal text-[#252525]">
                        {selectedPhoneCountry.dialCode}
                      </span>
                      <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                    </button>
                  )}

                  <input
                    type="tel"
                    value={localPhoneNumber}
                    onChange={handlePhoneNumberChange}
                    placeholder="555-555-5555"
                    disabled={loading}
                    className="flex-1 px-4 text-[14px] font-normal leading-[18px] text-[#252525]
                      bg-transparent border-none outline-none
                      placeholder:text-[#6E6E6E]
                      disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  />
                </div>

                {isPhoneDropdownOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setIsPhoneDropdownOpen(false)}
                    />
                    <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white
                      border border-gray-300 rounded-lg shadow-lg overflow-hidden">
                      <input
                        type="text"
                        placeholder="Search countries..."
                        value={phoneSearchTerm}
                        onChange={(e) => setPhoneSearchTerm(e.target.value)}
                        className="w-full px-4 py-3 text-[14px] border-b border-gray-200
                          outline-none placeholder:text-[#6E6E6E]"
                        style={{ fontFamily: 'Poppins, sans-serif' }}
                      />
                      <div className="max-h-60 overflow-y-auto">
                        {filteredPhoneCountries.map((country) => (
                          <div
                            key={country.code}
                            onClick={() => handlePhoneCountrySelect(country)}
                            className={`flex items-center gap-3 px-4 py-3 cursor-pointer
                              hover:bg-gray-50 transition-colors
                              ${selectedPhoneCountry.code === country.code ? 'bg-green-50' : ''}`}
                          >
                            <span className="text-xl">{country.flag}</span>
                            <div className="flex-1 min-w-0">
                              <div className="text-[14px] text-[#252525] truncate"
                                style={{ fontFamily: 'Poppins, sans-serif' }}>
                                {country.name}
                              </div>
                            </div>
                            <span className="text-[14px] text-[#6E6E6E]"
                              style={{ fontFamily: 'Poppins, sans-serif' }}>
                              {country.dialCode}
                            </span>
                            {selectedPhoneCountry.code === country.code && (
                              <CheckIcon className="h-5 w-5 text-[#49C44F]" />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {error && (
                <p
                  className="text-[12px] text-red-600 mb-4"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  {error}
                </p>
              )}

              <button
                type="button"
                onClick={updatePhoneAndSend}
                disabled={loading || !localPhoneNumber}
                className={`
                  w-full h-[48px] text-white font-semibold text-[16px] leading-[20px]
                  bg-[#252525] rounded-lg
                  ${loading || !localPhoneNumber
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-[#3a3a3a] cursor-pointer'}
                `}
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {loading ? 'Confirming...' : 'Confirm'}
              </button>
            </>
          )}

          {step === STEPS.SUCCESS && (
            <div className="text-center">

              <div className="flex justify-center mb-6">
                <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                  <circle cx="32" cy="32" r="30" stroke="#49C44F" strokeWidth="3" fill="none" />
                  <path
                    d="M20 32L28 40L44 24"
                    stroke="#49C44F"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              <h2
                className="text-[20px] font-semibold leading-[26px] text-[#252525] mb-6"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                You have successfully verified your phone number.
              </h2>

              <div className="flex items-start gap-3 mb-6 text-left">
                <input
                  id="sms-tracking-optin"
                  type="checkbox"
                  checked={smsTrackingOptin}
                  onChange={(e) => setSmsTrackingOptin(e.target.checked)}
                  className="w-4 h-4 mt-0.5 text-[#49C44F] border-gray-300 rounded focus:ring-[#49C44F]"
                />
                <label
                  htmlFor="sms-tracking-optin"
                  className="text-[14px] font-normal leading-[18px] text-[#252525]"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  Opt-in to SMS tracking updates
                </label>
              </div>

              <button
                type="button"
                onClick={handleDone}
                className="w-full h-[48px] text-white font-semibold text-[16px] leading-[20px]
                  bg-[rgba(28,41,56,1)] rounded-lg hover:bg-[#3a3a3a] cursor-pointer"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

MobileVerifyModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onVerified: PropTypes.func,
  phoneNumber: PropTypes.string,
  authToken: PropTypes.string,
  brandId: PropTypes.string
};

export default MobileVerifyModal;
